import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Draggable from 'react-draggable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// Utility: parse sample date/time using format from metadata
function getSampleDateObj(sample: any) {
  const dateStr = sample.metadata?.date;
  const timeStr = sample.metadata?.time;
  const dateFormat = sample.metadata?.date_format || 'DD/MM/YYYY';
  const timeFormat = sample.metadata?.time_format || 'HH:mm:ss';

  if (dateStr && timeStr) {
    // Try parsing with the exact format first
    let result = dayjs(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`);

    // If that fails, try with more flexible parsing
    if (!result.isValid()) {
      // Try different date formats that are more flexible
      const flexibleDateFormats = [
        'DD/MM/YYYY', 'D/M/YYYY', 'DD/MM/YY', 'D/M/YY',
        'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY'
      ];
      for (const format of flexibleDateFormats) {
        result = dayjs(`${dateStr} ${timeStr}`, `${format} ${timeFormat}`);
        if (result.isValid()) break;
      }
    }
    if (result.isValid()) return result;
  }
  if (dateStr) {
    let result = dayjs(dateStr, dateFormat);
    if (!result.isValid()) {
      const flexibleDateFormats = [
        'DD/MM/YYYY', 'D/M/YYYY', 'DD/MM/YY', 'D/M/YY',
        'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY'
      ];
      for (const format of flexibleDateFormats) {
        result = dayjs(dateStr, format);
        if (result.isValid()) break;
      }
    }
    if (result.isValid()) return result;
  }
  if (sample.sample_date) {
    const result = dayjs(sample.sample_date);
    if (result.isValid()) return result;
  }
  return dayjs('invalid');
}

// Utility: sort samples chronologically (newest to oldest)
function getChronologicalSamples(samples: any[]) {
  return [...samples].sort((a, b) => {
    const dateA = getSampleDateObj(a);
    const dateB = getSampleDateObj(b);
    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;
    return dateB.valueOf() - dateA.valueOf(); // newest to oldest
  });
}

interface DistanceMeasurementPopupProps {
  marker1Id: string;
  marker2Id: string;
  marker1Label: string;
  marker2Label: string;
  distance: number;
  onClose: () => void;
  position: { x: number; y: number };
  measurementData: any[];
  nominalData: any[];
  containerId?: string;
}

interface DistanceData {
  x_distance: number;
  y_distance: number;
  z_distance: number;
  three_d_distance: number;
  sample_date: string;
  sample_id: string;
}

const DistanceMeasurementPopup = ({
  marker1Id,
  marker2Id,
  marker1Label,
  marker2Label,
  distance,
  onClose,
  position,
  measurementData,
  nominalData,
  containerId = "stl-viewer-container"
}: DistanceMeasurementPopupProps) => {
  const [selectedCharacteristics, setSelectedCharacteristics] = useState<string[]>([]);
  const [distanceData, setDistanceData] = useState<DistanceData[]>([]);

  // Calculate distance data from measurement data
  useEffect(() => {
    const calculatedData: DistanceData[] = [];
    
    // Group measurements by sample
    const samples = new Map<string, any[]>();
    measurementData.forEach(measurement => {
      if (!samples.has(measurement.sample_id)) {
        samples.set(measurement.sample_id, []);
      }
      samples.get(measurement.sample_id)!.push(measurement);
    });

    // Calculate distances for each sample
    samples.forEach((sampleMeasurements, sampleId) => {
      const marker1Measurements = sampleMeasurements.filter(m => 
        m.feature_label === marker1Label || m.feature_label === marker1Id
      );
      const marker2Measurements = sampleMeasurements.filter(m => 
        m.feature_label === marker2Label || m.feature_label === marker2Id
      );

      if (marker1Measurements.length && marker2Measurements.length) {
        // Helper to get value for a coordinate (X, Y, Z) for a marker
        const getCoord = (measurements: any[], coord: string) => {
          const m = measurements.find(m => m.characteristic === coord);
          if (!m) return 0;
          if (typeof m.actual_value === 'number' && !isNaN(m.actual_value)) {
            return m.actual_value;
          } else if (typeof m.deviation === 'number' && !isNaN(m.deviation)) {
            return Math.abs(m.deviation); // Use absolute deviation if actual is missing
          }
          return 0;
        };

        const marker1X = getCoord(marker1Measurements, 'X');
        const marker1Y = getCoord(marker1Measurements, 'Y');
        const marker1Z = getCoord(marker1Measurements, 'Z');
        const marker2X = getCoord(marker2Measurements, 'X');
        const marker2Y = getCoord(marker2Measurements, 'Y');
        const marker2Z = getCoord(marker2Measurements, 'Z');

        // Calculate distances
        const x_distance = Math.abs(marker2X - marker1X);
        const y_distance = Math.abs(marker2Y - marker1Y);
        const z_distance = Math.abs(marker2Z - marker1Z);
        const three_d_distance = Math.sqrt(
          x_distance * x_distance +
          y_distance * y_distance +
          z_distance * z_distance
        );

        // Use the robustly parsed measurement date from metadata
        const parsedDate = getSampleDateObj(marker1Measurements[0]);
        calculatedData.push({
          sample_id: sampleId,
          sample_date: parsedDate.toISOString(),
          x_distance,
          y_distance,
          z_distance,
          three_d_distance
        });
      }
    });

    // Sort the calculated data chronologically (oldest to newest for display)
    const sortedData = [...calculatedData].sort((a, b) => {
      const dateA = getSampleDateObj(a);
      const dateB = getSampleDateObj(b);
      return dateA.valueOf() - dateB.valueOf(); // oldest to newest for chart display
    });

    setDistanceData(sortedData);
  }, [measurementData, marker1Id, marker2Id, marker1Label, marker2Label]);

  const handleCharacteristicToggle = (characteristic: string) => {
    setSelectedCharacteristics(prev => 
      prev.includes(characteristic)
        ? prev.filter(c => c !== characteristic)
        : [...prev, characteristic]
    );
  };

  const getStatistics = (characteristic: string) => {
    const values = distanceData.map(d => d[characteristic as keyof DistanceData] as number);
    if (values.length === 0) return null;

    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    const min = Math.min(...values);
    const max = Math.max(...values);

    return { mean, stdDev, min, max, values };
  };

  const getNominalValue = (characteristic: string) => {
    // Find nominal value for this characteristic
    const nominal = nominalData.find(n => 
      n.feature_label === `${marker1Label}_to_${marker2Label}` || 
      n.feature_label === `${marker1Id}_to_${marker2Id}` ||
      n.characteristic === characteristic
    );
    return nominal?.nominal_value || null;
  };

  const calculateCpCpk = (characteristic: string, nominalValue: number | null) => {
    if (!nominalValue) return { cp: null, cpk: null };

    const stats = getStatistics(characteristic);
    if (!stats) return { cp: null, cpk: null };

    // Find tolerance from nominal data
    const nominal = nominalData.find(n => 
      n.feature_label === `${marker1Label}_to_${marker2Label}` || 
      n.feature_label === `${marker1Id}_to_${marker2Id}` ||
      n.characteristic === characteristic
    );

    const upperTolerance = nominal?.upper_tolerance || nominalValue + 0.1;
    const lowerTolerance = nominal?.lower_tolerance || nominalValue - 0.1;
    const tolerance = upperTolerance - lowerTolerance;

    const cp = tolerance / (6 * stats.stdDev);
    const cpu = (upperTolerance - stats.mean) / (3 * stats.stdDev);
    const cpl = (stats.mean - lowerTolerance) / (3 * stats.stdDev);
    const cpk = Math.min(cpu, cpl);

    return { cp, cpk };
  };

  const getChartData = (characteristic: string) => {
    return distanceData.map((d, index) => ({
      sample: index + 1,
      value: d[characteristic as keyof DistanceData] as number,
      date: d.sample_date
    }));
  };

  // Calculate tolerance by summing tolerances of both markers
  const getToleranceForCharacteristic = (characteristic: string) => {
    // Map distance characteristic to coordinate characteristic
    const coordMap: { [key: string]: string } = {
      'x_distance': 'X',
      'y_distance': 'Y', 
      'z_distance': 'Z',
      'three_d_distance': 'X' // For 3D, we'll use X as base but calculate differently
    };

    const coordChar = coordMap[characteristic];
    if (!coordChar) return { upperTol: null, lowerTol: null };

    // Get tolerance data from nominalData for both markers
    const marker1Nominal = nominalData.find(n => 
      (n.feature_label === marker1Label || n.feature_label === marker1Id) && 
      n.characteristic === coordChar
    );
    const marker2Nominal = nominalData.find(n => 
      (n.feature_label === marker2Label || n.feature_label === marker2Id) && 
      n.characteristic === coordChar
    );

    if (!marker1Nominal || !marker2Nominal) {
      return { upperTol: null, lowerTol: null };
    }

    // For distance measurements, add the tolerances together
    // If marker1 X has upper tolerance 1.5 and marker2 X has upper tolerance 1.5,
    // then the distance upper tolerance should be 3.0
    const upperTol = (marker1Nominal.upper_tolerance || 0) + (marker2Nominal.upper_tolerance || 0);
    const lowerTol = (marker1Nominal.lower_tolerance || 0) + (marker2Nominal.lower_tolerance || 0);

    return { upperTol, lowerTol };
  };

  // Render chart like MeasurementPopup
  const renderChart = (characteristic: string) => {
    const values = distanceData.map(d => d[characteristic as keyof DistanceData] as number);
    if (values.length === 0) return null;

    const { upperTol, lowerTol } = getToleranceForCharacteristic(characteristic);
    const characteristicLabel = availableCharacteristics.find(c => c.key === characteristic)?.label || characteristic;

    // Calculate statistics
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Calculate Cp/Cpk if tolerances are available
    let cp = null;
    let cpk = null;
    if (upperTol !== null && lowerTol !== null && stdDev > 0) {
      const tolerance = upperTol - lowerTol;
      cp = tolerance / (6 * stdDev);
      const cpu = (upperTol - mean) / (3 * stdDev);
      const cpl = (mean - lowerTol) / (3 * stdDev);
      cpk = Math.min(cpu, cpl);
    }

    const getCpColor = (value: number) => {
      if (value < 1) return 'bg-red-500';
      if (value <= 1.33) return 'bg-orange-500';
      return 'bg-green-500';
    };

    // Create chart data structure
    const horiz = [...Array(values.length).keys()].map(x => x + 1);
    const chartData = values.map((value, i) => {
      const isInTolerance = upperTol !== null && lowerTol !== null 
        ? value >= lowerTol && value <= upperTol 
        : true;
      
      return {
        x: horiz[i],
        y: value,
        color: isInTolerance ? "green" : "red",
        sample: distanceData[i]?.sample_id || `Sample ${i + 1}`,
        characteristic: characteristic,
        value: value
      };
    });

    // Create tolerance line data
    const upperTolData = upperTol !== null ? horiz.map(x => ({ x, y: upperTol })) : [];
    const lowerTolData = lowerTol !== null ? horiz.map(x => ({ x, y: lowerTol })) : [];

    // Chart dimensions
    const chartWidth = 250;
    const chartHeight = 150;
    const margin = { top: 20, right: 15, bottom: 40, left: 25 };
    const innerWidth = chartWidth - margin.left - margin.right;
    const innerHeight = chartHeight - margin.top - margin.bottom;

    // Calculate scale
    const valueAbs = values.map(v => Math.abs(v));
    const tolBand = upperTol !== null && lowerTol !== null 
      ? [Math.abs(upperTol), Math.abs(lowerTol)]
      : [];
    
    const maxTol = tolBand.length > 0 ? Math.max(...tolBand) : 0;
    const maxVal = Math.max(...valueAbs);
    const yScale = maxTol >= maxVal ? maxTol : maxVal;

    return (
      <Card key={characteristic} className="border border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{characteristicLabel}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Statistics */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>Mean: {mean.toFixed(3)}</div>
            <div>StdDev: {stdDev.toFixed(3)}</div>
            <div>Min: {min.toFixed(3)}</div>
            <div>Max: {max.toFixed(3)}</div>
          </div>

          {/* Cp/Cpk */}
          {cp !== null && cpk !== null && (
            <div className="flex gap-2">
              <Badge variant={cp >= 1.33 ? "default" : "destructive"}>
                Cp: {cp.toFixed(2)}
              </Badge>
              <Badge variant={cpk >= 1.33 ? "default" : "destructive"}>
                Cpk: {cpk.toFixed(2)}
              </Badge>
            </div>
          )}

          {/* Chart */}
          <div className="mt-4">
            <div className="text-sm font-medium mb-2">
              {characteristicLabel} Trend ({values.length} samples)
            </div>
            
            <svg width={chartWidth} height={chartHeight} className="border border-gray-300 bg-white">
              {/* Chart area background */}
              <rect
                x={margin.left}
                y={margin.top}
                width={innerWidth}
                height={innerHeight}
                fill="none"
                stroke="black"
                strokeWidth="0.5"
              />
              
              {/* Create scales */}
              <g>
                {/* X-axis */}
                {horiz.map((x, i) => (
                  <g key={`x-axis-${i}`}>
                    <line
                      x1={margin.left + (i / (horiz.length - 1)) * innerWidth}
                      y1={margin.top + innerHeight}
                      x2={margin.left + (i / (horiz.length - 1)) * innerWidth}
                      y2={margin.top + innerHeight + 5}
                      stroke="black"
                      strokeWidth="0.5"
                    />
                    <text
                      x={margin.left + (i / (horiz.length - 1)) * innerWidth}
                      y={margin.top + innerHeight + 15}
                      textAnchor="middle"
                      fontSize="8"
                      fill="black"
                    >
                      {x}
                    </text>
                  </g>
                ))}
                
                {/* Y-axis ticks */}
                {[-1.5 * yScale, -yScale, -0.5 * yScale, 0, 0.5 * yScale, yScale, 1.5 * yScale].map((tickValue, i) => {
                  const yPos = margin.top + innerHeight - ((tickValue + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                  return (
                    <g key={`y-axis-${i}`}>
                      <line
                        x1={margin.left - 5}
                        y1={yPos}
                        x2={margin.left}
                        y2={yPos}
                        stroke="black"
                        strokeWidth="0.5"
                      />
                      <text
                        x={margin.left - 8}
                        y={yPos + 2}
                        textAnchor="end"
                        fontSize="8"
                        fill="black"
                      >
                        {tickValue.toFixed(1)}
                      </text>
                    </g>
                  );
                })}
                
                {/* Upper tolerance line */}
                {upperTolData.length > 0 && (
                  <path
                    d={upperTolData.map((d, i) => {
                      const x = margin.left + (i / (horiz.length - 1)) * innerWidth;
                      const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="red"
                    strokeWidth="1"
                  />
                )}
                
                {/* Lower tolerance line */}
                {lowerTolData.length > 0 && (
                  <path
                    d={lowerTolData.map((d, i) => {
                      const x = margin.left + (i / (horiz.length - 1)) * innerWidth;
                      const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="red"
                    strokeWidth="1"
                  />
                )}
                
                {/* Data line */}
                <path
                  d={chartData.map((d, i) => {
                    const x = margin.left + (i / (horiz.length - 1)) * innerWidth;
                    const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="blue"
                  strokeWidth="1"
                />
                
                {/* Data points */}
                {chartData.map((d, i) => {
                  const x = margin.left + (i / (horiz.length - 1)) * innerWidth;
                  const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                  
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="3"
                      fill={d.color}
                      stroke="none"
                      strokeWidth="1"
                      style={{ cursor: 'pointer' }}
                      onMouseOver={(e) => {
                        const tooltip = document.getElementById(tooltipId);
                        if (tooltip) {
                          tooltip.innerHTML = `Sample: ${d.sample}<br/>Characteristic: ${d.characteristic}<br/>Distance: ${d.value.toFixed(3)}`;
                          tooltip.style.opacity = '1';
                          
                          // Position tooltip relative to container using transform
                          const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
                          const containerRect = container.getBoundingClientRect();
                          const mouseX = e.clientX - containerRect.left;
                          const mouseY = e.clientY - containerRect.top;
                          
                          tooltip.style.left = mouseX + 'px';
                          tooltip.style.top = mouseY + 'px';
                          tooltip.style.transform = 'translate(10px, 15px)'; // Slightly to the right and below cursor
                        }
                      }}
                      onMouseMove={(e) => {
                        const tooltip = document.getElementById(tooltipId);
                        if (tooltip) {
                          // Update tooltip position relative to container using transform
                          const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
                          const containerRect = container.getBoundingClientRect();
                          const mouseX = e.clientX - containerRect.left;
                          const mouseY = e.clientY - containerRect.top;
                          
                          tooltip.style.left = mouseX + 'px';
                          tooltip.style.top = mouseY + 'px';
                          tooltip.style.transform = 'translate(10px, 15px)'; // Slightly to the right and below cursor
                        }
                      }}
                      onMouseLeave={() => {
                        const tooltip = document.getElementById(tooltipId);
                        if (tooltip) {
                          tooltip.style.opacity = '0';
                        }
                      }}
                    />
                  );
                })}
              </g>
            </svg>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Calculate available characteristics based on actual measurements
  const getAvailableCharacteristics = () => {
    const available: { key: string; label: string }[] = [];
    
    // Get all measurements for both markers
    const marker1Measurements = measurementData.filter(m => 
      m.feature_label === marker1Label || m.feature_label === marker1Id
    );
    const marker2Measurements = measurementData.filter(m => 
      m.feature_label === marker2Label || m.feature_label === marker2Id
    );

    // Check which coordinates are available for both markers
    const marker1HasX = marker1Measurements.some(m => m.characteristic === 'X');
    const marker1HasY = marker1Measurements.some(m => m.characteristic === 'Y');
    const marker1HasZ = marker1Measurements.some(m => m.characteristic === 'Z');
    
    const marker2HasX = marker2Measurements.some(m => m.characteristic === 'X');
    const marker2HasY = marker2Measurements.some(m => m.characteristic === 'Y');
    const marker2HasZ = marker2Measurements.some(m => m.characteristic === 'Z');

    // X Distance: both markers need X coordinates
    if (marker1HasX && marker2HasX) {
      available.push({ key: 'x_distance', label: 'X Distance' });
    }

    // Y Distance: both markers need Y coordinates
    if (marker1HasY && marker2HasY) {
      available.push({ key: 'y_distance', label: 'Y Distance' });
    }

    // Z Distance: both markers need Z coordinates
    if (marker1HasZ && marker2HasZ) {
      available.push({ key: 'z_distance', label: 'Z Distance' });
    }

    // 3D Distance: all three coordinates needed for both markers
    if (marker1HasX && marker1HasY && marker1HasZ && 
        marker2HasX && marker2HasY && marker2HasZ) {
      available.push({ key: 'three_d_distance', label: '3D Distance' });
    }

    return available;
  };

  const availableCharacteristics = getAvailableCharacteristics();

  // Auto-select first available characteristic when popup opens
  useEffect(() => {
    if (availableCharacteristics.length > 0 && selectedCharacteristics.length === 0) {
      setSelectedCharacteristics([availableCharacteristics[0].key]);
    }
  }, [availableCharacteristics.length, selectedCharacteristics.length]);

  const draggableRef = useRef(null);
  const [svgLine, setSvgLine] = useState<HTMLElement | null>(null);

  // Create unique line ID for this popup instance
  const lineId = `distance-popup-line_${marker1Id}_${marker2Id}`;
  const tooltipId = `distance-popup-tooltip_${marker1Id}_${marker2Id}`;

  // Function to update line position
  const updateLinePosition = () => {
    if (!svgLine || !draggableRef.current) return;

    const popupRect = draggableRef.current.getBoundingClientRect();
    const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Find the green distance line to get its center position
    const distanceLine = document.querySelector('.distance-line') as HTMLElement;
    const distanceLabel = document.querySelector('.distance-label') as HTMLElement;
    
    if (!distanceLine || !distanceLabel) return;
    
    // Get the center position of the green distance line (where the label is)
    const labelRect = distanceLabel.getBoundingClientRect();
    const lineCenterX = labelRect.left - containerRect.left + labelRect.width / 2;
    const lineCenterY = labelRect.top - containerRect.top + labelRect.height / 2;
    
    const popupCenterX = popupRect.left - containerRect.left + popupRect.width / 2;
    const popupCenterY = popupRect.top - containerRect.top + popupRect.height / 2;
    
    // Calculate distance and angle
    const distance = Math.sqrt(Math.pow(popupCenterX - lineCenterX, 2) + Math.pow(popupCenterY - lineCenterY, 2));
    const angle = Math.atan2(popupCenterY - lineCenterY, popupCenterX - lineCenterX) * 180 / Math.PI;
    
    // Position the line in container-relative coordinates
    svgLine.style.left = lineCenterX + 'px';
    svgLine.style.top = lineCenterY + 'px';
    svgLine.style.width = distance + 'px';
    svgLine.style.transform = `rotate(${angle}deg)`;
  };

  useEffect(() => {
    // Create CSS line element for connecting popup to distance line center
    const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
    
    // Check if line already exists to prevent recreation on re-renders
    let lineDiv = document.querySelector(`.${lineId}`) as HTMLDivElement;
    if (!lineDiv) {
      lineDiv = document.createElement('div');
      lineDiv.setAttribute('class', `${lineId}`);
      lineDiv.style.position = 'absolute';
      lineDiv.style.backgroundColor = '#ff0000'; // Bright red
      lineDiv.style.height = '2px'; // Same thickness as measurement popup
      lineDiv.style.boxShadow = '0 0 2px #ff0000'; // Same glow effect
      lineDiv.style.transformOrigin = '0 50%';
      lineDiv.style.pointerEvents = 'none';
      lineDiv.style.zIndex = '1'; // Lower z-index to appear behind popup
      lineDiv.style.opacity = '1';
      lineDiv.style.isolation = 'isolate';
      lineDiv.style.overflow = 'visible';
      
      // Append to container for coordinate system alignment
      container.appendChild(lineDiv);
    }
    
    // Create tooltip element and append to parent container
    let tooltip = document.getElementById(tooltipId) as HTMLDivElement;
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.setAttribute('id', tooltipId);
      tooltip.setAttribute('class', 'tooltip');
      tooltip.style.position = 'absolute';
      tooltip.style.zIndex = '10'; // Appropriate z-index for tooltip
      tooltip.style.fontFamily = "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif";
      tooltip.style.fontSize = 'small';
      tooltip.style.backgroundColor = 'white';
      tooltip.style.border = '1px solid #ccc';
      tooltip.style.padding = '8px';
      tooltip.style.borderRadius = '4px';
      tooltip.style.pointerEvents = 'none';
      tooltip.style.transition = 'opacity 0.15s ease-in-out';
      tooltip.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
      tooltip.style.opacity = '0';
      
      container.appendChild(tooltip);
    }
    
    setSvgLine(lineDiv);
    
    // Update position after popup is rendered
    const timeoutId = setTimeout(updateLinePosition, 50);

    return () => {
      clearTimeout(timeoutId);
      // Remove line and tooltip when component unmounts
      if (lineDiv && lineDiv.parentNode) {
        lineDiv.parentNode.removeChild(lineDiv);
      }
      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    };
  }, [containerId, lineId, tooltipId]);

  useEffect(() => {
    // Update line position when popup moves or line reference changes
    updateLinePosition();
  }, [draggableRef, svgLine, updateLinePosition]);

  return (
    <Draggable
      nodeRef={draggableRef}
      axis="both"
      defaultPosition={{ x: position.x, y: position.y }}
      bounds={`#${containerId}`}
      onDrag={(e) => updateLinePosition()}
    >
      <div
        ref={draggableRef}
        className="absolute bg-white border shadow-lg rounded-lg w-80 max-h-96 overflow-y-auto"
        style={{
          cursor: 'grab',
          zIndex: 10
        }}
      >
        <div className="p-3 relative">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="text-sm font-semibold">
                Distance: {marker1Label} to {marker2Label}
              </h3>
              <p className="text-xs text-gray-500">
                {distance.toFixed(3)} mm (3D Model)
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Characteristic Selection */}
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Select Characteristics:</h4>
            <div className="space-y-2">
              {availableCharacteristics.map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={selectedCharacteristics.includes(key)}
                    onCheckedChange={() => handleCharacteristicToggle(key)}
                  />
                  <label 
                    htmlFor={key}
                    className="text-sm cursor-pointer"
                  >
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Statistics for each selected characteristic */}
          <div className="space-y-4 overflow-visible">
            {selectedCharacteristics.map(characteristic => {
              return renderChart(characteristic);
            })}
          </div>

          {selectedCharacteristics.length === 0 && (
            <div className="text-center text-gray-500 text-sm py-4">
              Select characteristics to view distance data
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};

export default DistanceMeasurementPopup; 